// TabEditor — rearrange your own bottom tab bar.
//
// Opened from More → Edit tabs. Two lists: what is on the bar, in order, with
// up/down controls; and everything else this role can reach, to add from.
//
// ── Why up/down and not drag ────────────────────────────────────────────────
//
// Drag-and-drop on a phone competes with the scroll gesture and with the
// sheet's own dismiss, needs a long-press to disambiguate, and is close to
// untestable without a real touch device — which in this repo means untested,
// and CLAUDE.md is explicit that a screen is done only when it has been driven
// in a browser. Two buttons per row work with a thumb, work with a keyboard,
// work with a screen reader, and can be asserted. If drag is ever added it
// should be ON TOP of these, never instead of them.
//
// Nothing here writes to storage as you go. The bar changes only on Save, so
// a half-finished rearrangement is not something you have to undo — and Reset
// is always one tap from the defaults.

import React, { useMemo, useState } from 'react';
import { Sheet, FwButton, SectionLabel, Notice } from '../../ui/fw';
import { Icon } from '../../ui';
import { barLabel } from '../shared/navigation';
import { TAB_LIMIT, moveKey, toggleKey } from '../shared/tabPrefs';

/**
 * @param open        whether the sheet is showing
 * @param onClose     dismiss without saving
 * @param reachable   destinationsFor(role) — everything this person can open
 * @param current     the destinations on the bar right now, in order
 * @param onSave      (keys) => void
 * @param onReset     () => void — back to this role's defaults
 * @param canPersist  false when localStorage is unavailable, so the sheet can
 *                    say so instead of appearing to save and forgetting
 */
export default function TabEditor({ open, onClose, reachable, current, onSave, onReset, canPersist = true }) {
  const [keys, setKeys] = useState(() => (current || []).map((d) => d.key));

  // Reopening must start from what is actually on the bar, not from whatever
  // this component was last left holding.
  //
  // The seed is the bar this session was OPENED against, and `null` while the
  // sheet is closed — which is what makes a dismissal discard. Comparing the
  // seed to `currentKeys` alone is not enough and was the bug: dismissing
  // without saving leaves the bar unchanged, so `currentKeys` is unchanged,
  // so nothing re-seeded and reopening showed the rearrangement that had just
  // been abandoned. Pressing Save then committed it. The header above promises
  // the opposite — "the bar changes only on Save, so a half-finished
  // rearrangement is not something you have to undo" — and undoing it was
  // exactly what a person had to do.
  //
  // AppShell mounts this permanently (so closing it cannot unmount an open
  // sheet), which is why the state survives at all. Nulling on close still
  // handles the other direction too: a role change or a save WHILE open
  // re-seeds, because `seeded` no longer matches.
  const currentKeys = (current || []).map((d) => d.key).join(',');
  const [seeded, setSeeded] = useState(open ? currentKeys : null);
  if (open && seeded !== currentKeys) {
    setSeeded(currentKeys);
    setKeys((current || []).map((d) => d.key));
  }
  if (!open && seeded !== null) setSeeded(null);

  const byKey = useMemo(() => new Map((reachable || []).map((d) => [d.key, d])), [reachable]);
  const chosen = keys.map((k) => byKey.get(k)).filter(Boolean);
  const rest = (reachable || []).filter((d) => !keys.includes(d.key));
  const full = keys.length >= TAB_LIMIT;

  const move = (i, delta) => setKeys((k) => moveKey(k, i, delta));
  const toggle = (key) => setKeys((k) => toggleKey(k, key, TAB_LIMIT));

  return (
    <Sheet open={open} title="Edit tabs" onClose={onClose} className="fw-tabedit">
      <p className="fw-tabedit__hint">
        Pick up to {TAB_LIMIT} shortcuts for the bar at the bottom of the screen.
        Everything else stays one tap away under More.
      </p>

      {!canPersist ? (
        <Notice tone="warn">
          This phone will not let the app remember settings, so any change here
          lasts until you close the app. Private browsing is the usual cause.
        </Notice>
      ) : null}

      <div className="fw-tabedit__group">
        <SectionLabel>On the bar</SectionLabel>
        {chosen.length === 0 ? (
          <p className="fw-tabedit__empty">
            No tabs — the bar will show only More. Add one below.
          </p>
        ) : null}
        {chosen.map((d, i) => (
          <div key={d.key} className="fw-tabedit__row" data-tab-key={d.key}>
            <Icon name={d.icon} />
            <span className="fw-tabedit__name">
              {d.label}
              {barLabel(d) !== d.label ? (
                <span className="fw-tabedit__as">shows as “{barLabel(d)}”</span>
              ) : null}
            </span>
            <div className="fw-tabedit__moves">
              <button
                type="button"
                className="fw-tabedit__move"
                data-action="up"
                aria-label={`Move ${d.label} earlier`}
                disabled={i === 0}
                onClick={() => move(i, -1)}
              >
                <Icon name="chevronUp" />
              </button>
              <button
                type="button"
                className="fw-tabedit__move"
                data-action="down"
                aria-label={`Move ${d.label} later`}
                disabled={i === chosen.length - 1}
                onClick={() => move(i, 1)}
              >
                <Icon name="chevronDown" />
              </button>
              <button
                type="button"
                className="fw-tabedit__move fw-tabedit__move--off"
                data-action="remove"
                aria-label={`Take ${d.label} off the bar`}
                onClick={() => toggle(d.key)}
              >
                <Icon name="x" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="fw-tabedit__group">
        <SectionLabel>Add a tab</SectionLabel>
        {full ? (
          <p className="fw-tabedit__empty" data-note="full">
            The bar is full. Take one off above to add another.
          </p>
        ) : null}
        {rest.map((d) => (
          <button
            key={d.key}
            type="button"
            className="fw-tabedit__add"
            data-add-key={d.key}
            disabled={full}
            onClick={() => toggle(d.key)}
          >
            <Icon name={d.icon} />
            <span className="fw-tabedit__name">{d.label}</span>
            {!full ? <Icon name="plus" /> : null}
          </button>
        ))}
      </div>

      <div className="fw-tabedit__actions">
        <FwButton variant="ghost" onClick={onReset} data-action="reset">Reset to default</FwButton>
        <FwButton variant="primary" onClick={() => onSave(keys)} data-action="save">Save</FwButton>
      </div>
    </Sheet>
  );
}
